/**
 * Supplier-quotation derivations the FRONTEND owns (infra C.4: "the UOM-mismatch alert is
 * FE from `uom_mismatch: true`").
 *
 * The division of labour matters here. The SERVER decides *whether* a supplier's UOM differs
 * from the RFQ's — it holds both documents and sets `uom_mismatch`. The CLIENT decides *how
 * to explain it*, converting with the factor from `item.uom_conversions` so the purchaser
 * reads "1 box = 24 pcs" instead of two bare unit names. This file is that second half.
 */

import { uomFactor } from './format'
import type { RfqReply } from './api/useRfqReplies'
import type { Item, Rfq, SupplierQuotation, SupplierQuotationItem } from './types'

/** A flagged mismatch, with everything the banner needs to explain it in one sentence. */
export interface UomMismatch {
  sq_id: string
  supplier: string
  item_code: string
  item_name: string
  rfq_uom: string
  sq_uom: string
  /** How many RFQ-UOM units are in one quoted unit; `null` when the master can't say. */
  factor: number | null
}

/**
 * How many RFQ-UOM units make up one quoted unit — the only factor this screen may show,
 * because the sentence it appears in is "1 {sq_uom} = N {rfq_uom}".
 *
 * The master states every UOM relative to the item's BASE UOM, and the RFQ's UOM is often
 * not the base one (the seeded water bottle has base `no` but is enquired in `pack`), so the
 * two factors must be divided. Using the quoted UOM's base factor directly would be right
 * only when the RFQ happens to be in base units, and silently wrong otherwise.
 */
function factorBetweenUoms(master: Item, fromUom: string, toUom: string): number | null {
  const from = uomFactor(master, fromUom)
  const to = uomFactor(master, toUom)
  if (from === null || to === null || to === 0) return null
  return from / to
}

/**
 * Every mismatch across all replies, in the order they'd be read.
 *
 * Trusts the server's `uom_mismatch` flag rather than re-comparing the strings — the server
 * is authoritative, and a UOM can differ in ways a string compare would miss (aliases,
 * casing) that only the item master resolves.
 *
 * `supplierNames` is an optional code→display-name lookup (build one from the enquiry's RFQs,
 * whose `supplier_name` the server does send — see `pairRepliesToRfqs`'s own header for why
 * `SupplierQuotation.supplier` itself is a bare code as of task 9). A code with no entry shows
 * as-is; that is a real resolution through a real record, not a guess, so it never falls back to
 * anything looser than "leave the code visible."
 */
export function collectUomMismatches(
  quotations: SupplierQuotation[],
  itemsByCode: Map<string, Item>,
  supplierNames: Map<string, string> = new Map(),
): UomMismatch[] {
  const out: UomMismatch[] = []
  for (const sq of quotations) {
    for (const item of sq.items) {
      if (!item.uom_mismatch) continue
      const master = itemsByCode.get(item.item_code)
      out.push({
        sq_id: sq.id,
        supplier: supplierNames.get(sq.supplier) ?? sq.supplier,
        item_code: item.item_code,
        item_name: master?.name ?? item.item_code,
        rfq_uom: item.rfq_uom,
        sq_uom: item.sq_uom,
        // The quoted UOM expressed in the RFQ's UOM — e.g. box → 24 pcs.
        factor: master ? factorBetweenUoms(master, item.sq_uom, item.rfq_uom) : null,
      })
    }
  }
  return out
}

/** "1 box = 24 pcs" — the conversion the purchaser must apply before comparing. */
export function conversionHint(mismatch: UomMismatch): string | null {
  if (mismatch.factor === null) return null
  return `1 ${mismatch.sq_uom} = ${mismatch.factor} ${mismatch.rfq_uom}`
}

/**
 * A supplier's rate restated in the RFQ's UOM, so two suppliers quoting in different pack
 * sizes can be read side by side. Returns `null` when the master has no factor — better a
 * blank than a wrong number.
 *
 * Display only: the comparison workbench does its own allocation math from the raw offers.
 */
export function ratePerRfqUom(item: SupplierQuotationItem, master: Item | undefined): number | null {
  if (!item.uom_mismatch) return item.rate.amount
  if (!master) return null
  const factor = factorBetweenUoms(master, item.sq_uom, item.rfq_uom)
  return factor && factor > 0 ? item.rate.amount / factor : null
}

/**
 * What has actually come back on one RFQ. Three states, not two:
 *
 * - `quoted`   — a `Supplier Quotation` exists; rates are in the system and in the comparison.
 * - `replied`  — the supplier answered and the mail is linked, but nobody has read it as a
 *                quotation yet. Measured live 2026-08-08: RFQ-2026-00011 sat here for a day,
 *                showing "Awaiting" with a Remind button, while the supplier's priced workbook
 *                was already attached to the reply below it.
 * - `awaiting` — genuinely nothing back.
 *
 * The middle state is the whole point. Collapsing it into `awaiting` is what let this screen offer
 * to chase a supplier who had already answered.
 */
export type ReplyState = 'quoted' | 'replied' | 'awaiting'

/**
 * A row's stable identity — the QUOTATION where there is one, else the RFQ.
 *
 * `rfq.id` alone is not unique across rows. One supplier can answer one RFQ several times, and
 * live ENQ-2026-00018 has five `Supplier Quotation`s all against RFQ-2026-00002 — so five rows
 * share that id. Keyed on it, React sees duplicate keys and an accordion opens all five at once.
 */
export function replyRowKey(row: ReplyRow): string {
  return row.quotation?.id ?? row.rfq.id
}

/** One row of the inbound table: an RFQ paired with its reply, if one has arrived. */
export interface ReplyRow {
  rfq: Rfq
  quotation: SupplierQuotation | null
  /** True when a `Supplier Quotation` exists — i.e. `state === 'quoted'`. Unchanged meaning. */
  received: boolean
  /** Linked inbound mail, newest first. Empty when none has arrived (or none was fetched). */
  replies: RfqReply[]
  state: ReplyState
}

/**
 * Pairs every RFQ sent with the reply that answers it, so the table shows outstanding
 * suppliers alongside the ones who have quoted — replies first, since those are actionable.
 *
 * Both sides are code-keyed now (task 8b for `Rfq.supplier`; task 9 for `SupplierQuotation.
 * supplier` — the new `supplier_quotation.get_supplier_quotations` row's `supplier` field IS
 * the `Supplier` Link value, not a display name), so pairing by `rfq.id` first and the shared
 * supplier CODE second is a genuine identity match, not the name-vs-code fallback this function
 * used to need to work around the old mapper still returning a display name. That fallback is
 * gone — no name-or-code leniency anywhere in this file, matching the rest of this migration:
 * a quotation whose `rfq`/`supplier` match nothing real fails to pair VISIBLY (it still shows,
 * as an unpaired reply below), rather than silently guessing a row via a display string.
 */
export function pairRepliesToRfqs(
  rfqs: Rfq[],
  quotations: SupplierQuotation[],
  /** Linked inbound mail per RFQ (`useRepliesByRfq`). Optional: callers that have not fetched it
   *  behave exactly as before, with every unquoted RFQ reading `awaiting`. */
  repliesByRfq: ReadonlyMap<string, RfqReply[]> = new Map(),
): ReplyRow[] {
  const byRfqId = new Map<string, SupplierQuotation>()
  const bySupplierCode = new Map<string, SupplierQuotation>()
  for (const sq of quotations) {
    if (sq.rfq) byRfqId.set(sq.rfq, sq)
    if (!bySupplierCode.has(sq.supplier)) bySupplierCode.set(sq.supplier, sq)
  }

  const rows = rfqs.map((rfq): ReplyRow => {
    const quotation = byRfqId.get(rfq.id) ?? bySupplierCode.get(rfq.supplier) ?? null
    const replies = repliesByRfq.get(rfq.id) ?? []
    return {
      rfq,
      quotation,
      received: quotation !== null,
      replies,
      state: quotation !== null ? 'quoted' : replies.length > 0 ? 'replied' : 'awaiting',
    }
  })

  // A reply that answers no known RFQ still has to be visible — losing a supplier's
  // quotation because its RFQ reference is missing would be worse than an odd row.
  const pairedIds = new Set(rows.map((r) => r.quotation?.id).filter(Boolean))
  for (const sq of quotations) {
    if (pairedIds.has(sq.id)) continue
    rows.push({
      // No RFQ reference to stand on, so the placeholder is keyed by the quotation itself —
      // several such replies must not collapse onto one row key. `sq.supplier` IS a real
      // `Supplier.code` now (task 9), so it carries the IDENTITY slot correctly; there is no
      // display name on this wire to put in `supplier_name` (see `useSupplierQuotations.ts`'s
      // module header), so it is left unset rather than fabricated from the code — the same
      // "resolve a display name through the paired RFQ, never guess one" rule this whole
      // function exists to enforce. A renderer falling back to `supplier_name ?? supplier`
      // shows the code here, honestly, same as it would for a real RFQ with no name on file.
      rfq: {
        id: sq.rfq ?? sq.id,
        supplier: sq.supplier,
        item_count: sq.items.length,
        excel_url: '',
        email_to: '',
      },
      quotation: sq,
      received: true,
      // This quotation answers no RFQ we know of, so there is no RFQ id to look its mail up by.
      replies: [],
      state: 'quoted',
    })
  }

  return rows.sort(
    (a, b) =>
      Number(b.received) - Number(a.received) ||
      (a.rfq.supplier_name ?? a.rfq.supplier).localeCompare(b.rfq.supplier_name ?? b.rfq.supplier),
  )
}

export interface ReplyProgress {
  sent: number
  received: number
  /**
   * Answered but not yet read as a quotation. Counted apart from both others, because the banner
   * used to fold these into `awaiting` and tell a purchaser "1 awaiting" about a supplier whose
   * priced workbook was already on screen.
   */
  replied: number
  awaiting: number
}

/** Counts for the banner: how much of the board has come back. */
export function replyProgress(rows: ReplyRow[]): ReplyProgress {
  const received = rows.filter((r) => r.state === 'quoted').length
  const replied = rows.filter((r) => r.state === 'replied').length
  return { sent: rows.length, received, replied, awaiting: rows.length - received - replied }
}

/* ============================================================================================
 * Turning a parse failure into something a purchaser can act on
 * ============================================================================================ */

/**
 * A readable explanation for a failed `parse_supplier_reply`, or `null` when we have nothing
 * better to say than the server's own words.
 *
 * A purchaser is not the audience for `MySQLdb.OperationalError (1292)`. Where we have positively
 * identified a server defect — measured, filed, and understood — we say what actually happened,
 * what it means for this supplier's quote, and what to do now. Everything else falls through to
 * the raw message rather than being dressed up: a wrong explanation is worse than a technical one,
 * because it sends someone looking in the wrong place.
 */
export function describeParseFailure(raw: string): string | null {
  const m = raw ?? ''

  // `B4`. The workbook we generate asks for "DELIVERY TIME (Days)" — a number — and the parser
  // writes it into `Supplier Quotation Item.delivery_date`, a Date column. Measured 2026-08-07
  // on the first real supplier-filled workbook ever put through this method.
  if (/delivery_date/i.test(m) && /Incorrect date value|1292/i.test(m)) {
    return (
      'The supplier filled in the "Delivery Time (Days)" column and the server cannot read it — ' +
      'it stores that column as a date, so a number of days is rejected. This is a known server ' +
      'bug (B4), not a problem with their workbook. Nothing was saved. Until it is fixed you can ' +
      'either enter this quotation by hand, or ask the supplier to resend with Delivery Time left ' +
      'blank.'
    )
  }

  /*
    `B5` — measured 2026-08-08 on RFQ-2026-00013:
    `ValidationError: Failed to parse Excel: name 'fappe' is not defined`.

    A Python NameError (`fappe` for `frappe`) surfacing through the server's own error wrapper. It
    is almost certainly B4's FIX, shipped with a typo in it: the evidence is that the two workbooks
    involved are byte-for-byte the same template, differing only in that Akhil's has DELIVERY TIME
    and SUPPLIER REMARKS filled in — and Shiby's, with both blank, parsed successfully at 09:02 the
    same morning. So the broken line sits on the path that only runs once a supplier uses the very
    field B4 was about.

    Matched on the undefined-name shape rather than the literal `fappe`, so the same class of
    server defect is still explained if the typo is a different word next time.
  */
  if (/is not defined/i.test(m) && /Failed to parse Excel|NameError/i.test(m)) {
    return (
      'The server crashed reading the workbook — a bug in its own code, not a problem with the ' +
      "supplier's file. Nothing was saved. It appears to affect replies where Delivery Time or " +
      'Supplier Remarks are filled in; a reply with both blank read successfully the same morning. ' +
      'Filed as B5. Their prices are shown on this screen, so you can enter this quotation by hand, ' +
      'or ask them to resend with those two columns blank.'
    )
  }

  return null
}
